import type {
  ChangeEvent,
  ClipboardEvent,
  CompositionEvent,
  CSSProperties,
  FocusEvent,
  FormEvent,
  KeyboardEvent,
  MouseEvent,
  MutableRefObject,
  PointerEventHandler,
  ReactNode
} from 'react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import styled, { css } from 'styled-components';
import type { FourValueTupleConvertable } from '@elfsight/widget-utils';
import { getInvertedColor, to4VTuple } from '@elfsight/widget-utils';
import equal from 'fast-deep-equal';
import TextareaAutosize from 'react-textarea-autosize';
import { TextMeasure } from '../../text-measure';
import {
  getVariantStyles,
  type StyleVariants
} from '../../utils/get-variant-styles';
import { toCSSProperties } from '../../utils/to-css-properties';
import { ProgressiveMask } from '../ProgressiveMask';
import { getLimitState } from './get-limit-state';

const LABEL_SCALE_FACTOR = 0.75;
const COLOR_TRANSPARENT = 'rgba(0, 0, 0, 0)';
const SLOT_MARGIN = 8;

export type TextControlBorderTypes = 'outline' | 'underline';

export type TextControlTypes =
  | 'text'
  | 'email'
  | 'number'
  | 'password'
  | 'tel'
  | 'url'
  | 'time';

export interface TextControlBaseInstance {
  containerRef: MutableRefObject<HTMLDivElement | null>;
  inputRef: MutableRefObject<HTMLTextAreaElement | HTMLInputElement | null>;
  focusInput: (options?: FocusOptions) => void;
}

type Variants =
  | 'initial'
  | 'touched'
  | 'filled'
  | 'focused'
  | 'disabled'
  | 'limitAlmostReached'
  | 'limitReached';
export type TextControlBaseVariants = StyleVariants<Variants>;

export type TextControlBaseSlotInner =
  | ReactNode
  | ((appliedVariants: string[]) => ReactNode);

export interface TextControlBaseProps {
  value?: string;
  initialValue?: string;
  label?: ReactNode;
  ariaLabel?: string;
  placeholder?: string;
  forceFocusAnimation?: boolean;
  leftSlot?: TextControlBaseSlotInner;
  rightSlot?: TextControlBaseSlotInner;
  limit?: number;
  multiline?: boolean;
  mask?: string;
  minVisibleRows?: number;
  maxVisibleRows?: number;
  autoFocus?: boolean;
  padding?: FourValueTupleConvertable<number>;
  variants?: TextControlBaseVariants;
  background?: string;
  fontStyle?: 'normal' | 'italic';
  fontWeight?: 'normal' | 'bold' | number;
  fontSize?: number;
  lineHeight?: number;
  shouldFocusSlotOnClick?: boolean;
  allowSlotNativeEvents?: boolean;
  specialEmptyCase?: string;
  fitContentWidth?: boolean;
  color?: string;
  borderType?: TextControlBorderTypes;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  underlineVariants?: TextControlBaseVariants;
  labelVariants?: TextControlBaseVariants;
  labelFontStyle?: 'normal' | 'italic';
  labelFontWeight?: 'normal' | 'bold' | number;
  labelFontSize?: number;
  labelTextTransform?: 'capitalize' | 'lowercase' | 'uppercase';
  labelColor?: string;
  placeholderVariants?: TextControlBaseVariants;
  placeholderFontStyle?: 'normal' | 'italic';
  placeholderFontWeight?: number;
  placeholderColor?: string;
  limitMessageVariants?: TextControlBaseVariants;
  limitMessageBackground?: string;
  customVariant?: string | string[];
  id?: string;
  name?: string;
  type?: TextControlTypes;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  className?: string;
  withoutTransition?: boolean;
  maxLength?: number;
  minLength?: number;
  inputMode?:
    | 'text'
    | 'email'
    | 'tel'
    | 'url'
    | 'none'
    | 'search'
    | 'numeric'
    | 'decimal'
    | undefined;
  style?: CSSProperties;
  onValueChange?: (
    value: string,
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onFocus?: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onClick?: (event: MouseEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onDoubleClick?: (
    event: MouseEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onKeyDown?: (
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onBlur?: (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
  onPaste?: (
    event: ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onKeyUp?: (
    event: KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onCompositionStart?: (
    event: CompositionEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onCompositionEnd?: (
    event: CompositionEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => void;
  onInput?: (event: FormEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
}

export const TextControlBase = memo(
  forwardRef<TextControlBaseInstance, TextControlBaseProps>(
    function TextControlBase(
      {
        value,
        mask,
        initialValue = '',
        label,
        ariaLabel,
        placeholder,
        forceFocusAnimation = false,
        leftSlot,
        rightSlot,
        limit,
        multiline = false,
        minVisibleRows = 3,
        maxVisibleRows = Infinity,
        autoFocus = false,
        shouldFocusSlotOnClick = true,
        allowSlotNativeEvents = false,
        specialEmptyCase,
        padding = [10, 0],
        variants = {} as TextControlBaseVariants,
        background = 'rgba(255, 255, 255, 0)',
        fontStyle = 'normal',
        fontWeight = 400,
        fontSize = 16,
        lineHeight = 1.32,
        fitContentWidth = false,
        color = 'rgb(17, 17, 17)',
        borderType,
        borderColor = 'rgba(17, 17, 17, 0.3)',
        borderWidth = 1,
        borderRadius,
        underlineVariants = {} as TextControlBaseVariants,
        labelVariants = {} as TextControlBaseVariants,
        labelFontStyle = 'normal',
        labelFontWeight = 400,
        labelFontSize,
        labelTextTransform,
        labelColor = 'rgba(17, 17, 17, 0.5)',
        placeholderVariants = {} as TextControlBaseVariants,
        placeholderFontStyle = 'normal',
        placeholderFontWeight = 400,
        placeholderColor = 'rgba(17, 17, 17, 0.3)',
        limitMessageVariants = {} as TextControlBaseVariants,
        limitMessageBackground = 'rgb(255, 255, 255)',
        customVariant,
        id,
        name,
        type = 'text',
        disabled = false,
        required = false,
        readOnly = false,
        className,
        withoutTransition = false,
        minLength,
        maxLength,
        inputMode = 'text',
        style,
        onPaste,
        onValueChange,
        onFocus,
        onClick,
        onKeyDown,
        onBlur,
        onKeyUp,
        onDoubleClick,
        onCompositionStart,
        onCompositionEnd,
        onInput
      },
      forwardingRef
    ) {
      const controlled = value !== undefined;
      const [localValue, setLocalValue] = useState(initialValue);
      const effectiveValue = controlled ? (value as string) : localValue;

      useEffect(() => {
        setLocalValue(initialValue);
      }, [initialValue]);

      const [nativeValid, setNativeValid] = useState(true);
      const emptyInvalid = !effectiveValue && !nativeValid; // native 'number' input reset value while invalid
      const specialEmpty =
        !!specialEmptyCase && effectiveValue === specialEmptyCase;
      const filled = !specialEmpty && (!!effectiveValue || emptyInvalid);

      const withLabel = !!label;
      const withPlaceholder = !!placeholder;
      const withOutlineBorders = borderType === 'outline';
      const withUnderline = borderType === 'underline';
      const withMask = !!mask;

      const containerRef = useRef<HTMLDivElement | null>(null);
      const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(
        null
      );

      const [focused, setFocused] = useState(false);

      const focusInput = useCallback((options?: FocusOptions) => {
        inputRef.current?.focus(options);
      }, []);

      useImperativeHandle(
        forwardingRef,
        () => ({
          containerRef,
          inputRef,
          focusInput
        }),
        [focusInput]
      );

      const format = useCallback(
        (input: string) =>
          (multiline ? input : input.replace(/\r?\n|\r/g, '')).slice(0, limit),
        [multiline, limit]
      );

      const handleChange = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
          const { value: rawValue } = event.target;
          const nextValue = format(rawValue);

          if (value === rawValue) {
            return;
          }

          if (!controlled) {
            setLocalValue(nextValue);
          }

          if (onValueChange) {
            onValueChange(nextValue, event);
          }
        },
        [controlled, format, onValueChange, value]
      );

      const handleInput = useCallback(
        (event: FormEvent<HTMLTextAreaElement | HTMLInputElement>) => {
          const valid = (event.target as HTMLTextAreaElement | HTMLInputElement)
            .validity.valid;

          setNativeValid(valid);

          if (onInput) {
            onInput(event);
          }
        },
        []
      );

      const handleInputFocus = useCallback(
        (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
          setFocused(true);

          if (onFocus) {
            onFocus(event);
          }
        },
        [onFocus]
      );

      const handleInputBlur = useCallback(
        (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
          setFocused(false);

          if (onBlur) {
            onBlur(event);
          }
        },
        [onBlur]
      );

      const handleContainerClick = useCallback(() => {
        focusInput();
      }, [focusInput]);

      const handleSlotPointerDown: PointerEventHandler<HTMLDivElement> =
        useCallback(
          (event) => {
            if (!shouldFocusSlotOnClick) {
              if (!allowSlotNativeEvents) {
                event.preventDefault();

                return;
              }

              return;
            }

            focusInput();
          },
          [focusInput, shouldFocusSlotOnClick]
        );

      const inputProps = {
        id,
        name,
        disabled,
        'aria-required': required,
        autoFocus,
        value: format(effectiveValue),
        placeholder,
        onChange: handleChange,
        onInput: handleInput,
        onFocus: handleInputFocus,
        onBlur: handleInputBlur,
        onCompositionStart,
        onCompositionEnd,
        onClick,
        onKeyDown,
        onKeyUp,
        onDoubleClick,
        onPaste,
        maxLength,
        minLength,
        inputMode,
        readOnly
      };

      const { length: valueLength } = effectiveValue;
      const { limitAlmostReached, limitReached } = getLimitState(
        valueLength,
        limit
      );

      // Applied variants calculation
      const appliedVariants = [
        'initial',
        (filled || focused || forceFocusAnimation) && 'touched',
        filled && 'filled',
        (focused || forceFocusAnimation) && 'focused',
        limitAlmostReached && 'limitAlmostReached',
        limitReached && 'limitReached',
        disabled && 'disabled',
        ...(Array.isArray(customVariant) ? customVariant : [customVariant])
      ].filter(Boolean) as Variants[];

      const [paddingTop, paddingRight, paddingBottom, paddingLeft] =
        to4VTuple(padding);

      const realLineHeight = Math.round(fontSize * lineHeight);
      const inputFilledYOffset = withLabel
        ? (realLineHeight * LABEL_SCALE_FACTOR) / 2
        : 0;

      const limitMessageColor = getInvertedColor(limitMessageBackground);

      // Slot renderer
      const renderSlot = useCallback(
        (inner: TextControlBaseSlotInner) => (
          <TextControlBaseSlot
            onPointerDown={handleSlotPointerDown}
            style={{ height: realLineHeight }}
          >
            <div>
              {typeof inner === 'function' ? inner(appliedVariants) : inner}
            </div>
          </TextControlBaseSlot>
        ),
        [appliedVariants, handleSlotPointerDown, realLineHeight]
      );

      return (
        <TextMeasure text={effectiveValue} textStyle={{ fontSize, fontWeight }}>
          {({ width }) => (
            <TextControlBaseContainer
              $paddingTop={paddingTop}
              $paddingLeft={paddingLeft}
              $paddingRight={paddingRight}
              $paddingBottom={paddingBottom}
              $variants={{
                ...variants,
                initial: {
                  borderColor: withOutlineBorders
                    ? borderColor
                    : COLOR_TRANSPARENT,
                  background,
                  color,
                  opacity: 1,
                  ...variants?.initial
                },
                disabled: {
                  opacity: 0.3,
                  ...variants?.disabled
                }
              }}
              $appliedVariants={appliedVariants}
              $withoutTransition={withoutTransition}
              ref={containerRef}
              className={className}
              style={{
                borderWidth,
                borderRadius,
                fontStyle,
                fontWeight,
                fontSize,
                lineHeight: `${realLineHeight}px`,
                width: fitContentWidth
                  ? width + paddingLeft + paddingRight + 4
                  : undefined,
                ...style
              }}
              onClick={handleContainerClick}
            >
              {limitAlmostReached && (
                <TextControlBaseLimitMessageWrapper
                  style={{ right: paddingRight }}
                  $visible={limitAlmostReached}
                  $withoutTransition={withoutTransition}
                >
                  <TextControlBaseLimitMessage
                    $variants={{
                      ...limitMessageVariants,
                      initial: {
                        background: limitMessageBackground,
                        color: limitMessageColor,
                        ...limitMessageVariants?.initial
                      }
                    }}
                    $appliedVariants={appliedVariants}
                    $withoutTransition={withoutTransition}
                  >
                    {valueLength}/{limit}
                  </TextControlBaseLimitMessage>
                </TextControlBaseLimitMessageWrapper>
              )}

              {!!leftSlot && renderSlot(leftSlot)}

              <TextControlBaseInner>
                {withLabel && (
                  <TextControlBaseLabelWrapper
                    $variants={{
                      initial: {
                        transform: 'translate(0, 0) scale(1)'
                      },
                      touched: {
                        transform: `translate(0, ${-inputFilledYOffset}px) scale(${LABEL_SCALE_FACTOR})`
                      }
                    }}
                    $appliedVariants={appliedVariants}
                    $withoutTransition={withoutTransition}
                  >
                    <TextControlBaseLabel
                      $variants={{
                        ...labelVariants,
                        initial: {
                          color: labelColor,
                          ...labelVariants?.initial
                        }
                      }}
                      $appliedVariants={appliedVariants}
                      $withoutTransition={withoutTransition}
                      htmlFor={id}
                      style={{
                        fontStyle: labelFontStyle,
                        fontWeight: labelFontWeight,
                        fontSize: labelFontSize,
                        textTransform: labelTextTransform
                      }}
                    >
                      {label}
                    </TextControlBaseLabel>
                  </TextControlBaseLabelWrapper>
                )}

                <TextControlBaseInputWrapper
                  $variants={
                    withLabel
                      ? {
                          initial: {
                            transform: `translateY(${inputFilledYOffset}px)`,
                            opacity: 0
                          },
                          touched: { opacity: 1 }
                        }
                      : {
                          initial: {
                            transform: 'translateY(0)',
                            opacity: 1
                          },
                          touched: { opacity: 1 }
                        }
                  }
                  $appliedVariants={appliedVariants}
                  $withoutTransition={withoutTransition}
                >
                  {withPlaceholder && !filled && (
                    <TextControlBasePlaceholder
                      $variants={{
                        ...placeholderVariants,
                        initial: {
                          color: placeholderColor,
                          ...placeholderVariants?.initial
                        }
                      }}
                      $appliedVariants={appliedVariants}
                      $withoutTransition={withoutTransition}
                      style={{
                        fontStyle: placeholderFontStyle,
                        fontWeight: placeholderFontWeight
                      }}
                    >
                      {placeholder}
                    </TextControlBasePlaceholder>
                  )}
                  {withMask && (
                    <TextControlBaseMask
                      $variants={{
                        ...placeholderVariants,
                        initial: {
                          color: placeholderColor,
                          ...placeholderVariants?.initial
                        }
                      }}
                      $appliedVariants={appliedVariants}
                      $withoutTransition={withoutTransition}
                      style={{
                        fontStyle: placeholderFontStyle,
                        fontWeight: placeholderFontWeight
                      }}
                    >
                      <ProgressiveMask
                        mask={mask}
                        value={effectiveValue}
                        fieldColor={placeholderColor}
                        fontWeight={fontWeight}
                        fontStyle={fontStyle}
                      />
                    </TextControlBaseMask>
                  )}
                  {multiline && (
                    <TextControlBaseTextarea
                      {...inputProps}
                      ref={inputRef as MutableRefObject<HTMLTextAreaElement>}
                      minRows={minVisibleRows}
                      maxRows={maxVisibleRows}
                      aria-label={ariaLabel}
                    />
                  )}
                  {!multiline && (
                    <TextControlBaseInput
                      {...inputProps}
                      ref={inputRef as MutableRefObject<HTMLInputElement>}
                      type={type}
                      autoComplete="off"
                      aria-label={ariaLabel}
                    />
                  )}
                </TextControlBaseInputWrapper>
              </TextControlBaseInner>

              {!!rightSlot && renderSlot(rightSlot)}

              {withUnderline && (
                <TextControlBaseUnderline
                  $variants={{
                    ...underlineVariants,
                    initial: {
                      borderColor,
                      ...underlineVariants?.initial
                    }
                  }}
                  $appliedVariants={appliedVariants}
                  $withoutTransition={withoutTransition}
                  style={{
                    top: -borderWidth,
                    right: -borderWidth,
                    bottom: -borderWidth,
                    left: -borderWidth,
                    borderWidth
                  }}
                />
              )}
            </TextControlBaseContainer>
          )}
        </TextMeasure>
      );
    }
  ),
  (prevProps, nextProps) => {
    const {
      leftSlot: prevLeftSlot,
      rightSlot: prevRightSlot,
      ...otherPrevProps
    } = prevProps;

    const {
      leftSlot: nextLeftSlot,
      rightSlot: nextRightSlot,
      ...otherNextProps
    } = nextProps;

    return (
      equal(otherPrevProps, otherNextProps) &&
      prevLeftSlot === nextLeftSlot &&
      prevRightSlot === nextRightSlot
    );
  }
);

const inheritFontStyles = css`
  font-style: inherit;
  font-variant: inherit;
  font-weight: inherit;
  font-size: inherit;
  line-height: inherit;
  font-family: inherit;
  color: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
`;

const input = css`
  ${inheritFontStyles};

  display: block;
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 0;
  border: none !important;
  outline: none !important;
  box-shadow: none;
  background: transparent !important;
  background-color: transparent !important;
  text-overflow: ellipsis;

  /* fixes transparent carriage issue in Safari  */
  transform: translate3d(0, 0, 0);

  &::placeholder {
    opacity: 0;
  }
`;

export const TextControlBaseContainer = styled.div<{
  $variants: TextControlBaseVariants;
  $appliedVariants: Variants[];
  $withoutTransition: boolean;
  $paddingLeft: number;
  $paddingRight: number;
  $paddingTop: number;
  $paddingBottom: number;
}>`
  ${({
    $variants,
    $appliedVariants,
    $withoutTransition,
    $paddingTop,
    $paddingRight,
    $paddingBottom,
    $paddingLeft
  }) => css`
    position: relative;
    display: flex;
    border: 1px solid;
    cursor: text;
    ${!$withoutTransition &&
    css`
      transition-property: opacity, transform, color, background-color;
      transition-duration: 0.1s;
      transition-timing-function: ease-in;
    `}
    padding: ${$paddingTop}px ${$paddingRight}px ${$paddingBottom}px ${$paddingLeft}px;
    ${toCSSProperties(getVariantStyles($variants, $appliedVariants))}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const TextControlBaseInner = styled.div`
  position: relative;
  flex: 1;
`;

export const TextControlBaseSlot = styled.div`
  display: flex;
  align-items: center;
  flex: 0;

  &:first-child {
    margin-right: ${SLOT_MARGIN}px;
  }

  &:last-child {
    margin-left: ${SLOT_MARGIN}px;
  }
`;

export const TextControlBaseInputWrapper = styled.div<{
  $variants: TextControlBaseVariants;
  $appliedVariants: Variants[];
  $withoutTransition: boolean;
}>`
  ${({ $variants, $appliedVariants, $withoutTransition }) => css`
    position: relative;
    z-index: 1;
    ${!$withoutTransition &&
    css`
      transition: all 0.1s ease-in;
    `}
    ${toCSSProperties(getVariantStyles($variants, $appliedVariants))}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const TextControlBaseInput = styled.input`
  input& {
    ${input}
  }
`;

export const TextControlBaseTextarea = styled(TextareaAutosize)`
  textarea& {
    ${input}
  }

  resize: none;
`;

export const TextControlBaseLabelWrapper = styled.div<{
  $variants: TextControlBaseVariants;
  $appliedVariants: Variants[];
  $withoutTransition: boolean;
}>`
  ${({ $variants, $appliedVariants, $withoutTransition }) => css`
    position: absolute;
    top: auto;
    left: auto;
    max-width: 100%;
    transform-origin: 2px 25%;
    pointer-events: none;
    ${!$withoutTransition &&
    css`
      transition: all 0.1s ease-in;
    `}
    ${toCSSProperties(getVariantStyles($variants, $appliedVariants))}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const TextControlBaseLabel = styled.label<{
  $variants: TextControlBaseVariants;
  $appliedVariants: Variants[];
  $withoutTransition: boolean;
}>`
  ${({ $variants, $appliedVariants, $withoutTransition }) => css`
    ${inheritFontStyles};

    display: block;
    overflow: hidden;
    max-width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    ${!$withoutTransition &&
    css`
      transition: all 0.1s ease-in;
    `}
    ${toCSSProperties(getVariantStyles($variants, $appliedVariants))}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const TextControlBasePlaceholder = styled.div<{
  $variants: TextControlBaseVariants;
  $appliedVariants: Variants[];
  $withoutTransition: boolean;
}>`
  ${({ $variants, $appliedVariants, $withoutTransition }) => css`
    ${inheritFontStyles};

    position: absolute;
    overflow: hidden;
    top: 0;
    right: 0;
    left: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    pointer-events: none;
    ${!$withoutTransition &&
    css`
      transition: all 0.1s ease-in;
    `}
    ${toCSSProperties(getVariantStyles($variants, $appliedVariants))}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const TextControlBaseMask = styled.div<{
  $variants: TextControlBaseVariants;
  $appliedVariants: Variants[];
  $withoutTransition: boolean;
}>`
  ${({ $variants, $appliedVariants, $withoutTransition }) => css`
    ${inheritFontStyles};

    position: absolute;
    overflow: hidden;
    top: 0;
    right: 0;
    left: 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    pointer-events: none;
    ${!$withoutTransition &&
    css`
      transition: all 0.1s ease-in;
    `}
    ${toCSSProperties(getVariantStyles($variants, $appliedVariants))}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const TextControlBaseLimitMessageWrapper = styled.div<{
  $visible: boolean;
  $withoutTransition: boolean;
}>`
  ${({ $visible, $withoutTransition }) => css`
    position: absolute;
    z-index: 3;
    top: -14px;
    pointer-events: none;
    opacity: ${$visible ? 1 : 0};
    ${!$withoutTransition &&
    css`
      transition: opacity 0.1s ease-in;
    `}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const TextControlBaseLimitMessage = styled.div<{
  $variants: TextControlBaseVariants;
  $appliedVariants: Variants[];
  $withoutTransition: boolean;
}>`
  ${({ $variants, $appliedVariants, $withoutTransition }) => css`
    padding: 5px 8px;
    font-size: 13px;
    line-height: 1.38;
    border-radius: 4px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.3);
    ${!$withoutTransition &&
    css`
      transition: all 0.1s ease-in;
    `}
    ${toCSSProperties(getVariantStyles($variants, $appliedVariants))}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;

export const TextControlBaseUnderline = styled.div<{
  $variants: TextControlBaseVariants;
  $appliedVariants: Variants[];
  $withoutTransition: boolean;
}>`
  ${({ $variants, $appliedVariants, $withoutTransition }) => css`
    position: absolute;
    z-index: 2;
    border-bottom: solid;
    border-radius: inherit;
    pointer-events: none;
    ${!$withoutTransition &&
    css`
      transition: all 0.1s ease-in;
    `}
    ${toCSSProperties(getVariantStyles($variants, $appliedVariants))}

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;
